import {
    EMOTORAD__EVENT__CALLS__CALL_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__CURRENT_PLATFORM__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__CUSTOMER_CITY__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__CUSTOMER_PINCODE__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__CUSTOMER_STATE__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__LAST_CALL_DATE__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__LOGGED_CALL_REMARKS__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__SCHEDULED_BY__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__SCHEDULED_ON__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__STATUS__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CALLS_COUNT__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_CITY__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_PINCODE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_STATE__COLUMN_ID,
    EMOTORAD_CALL_EVENT_SUBMIT_CURRENT_PLATFORM_VALUE,
    EmotoradCallStatusEnum,
    EmotoradEventTypes,
    resolveEmotoradLastLoggedCallDispositionId,
} from "~/domainModels/workspaceSpecific/emotorad";
import {
    buildScheduleCallTimeSlotOptions,
    combineScheduleDateAndTimeSlot,
    copyJourneyColumnToEventWhenPresent,
    parseScheduleNumericColumn,
} from "~/domainModels/scheduleCall/scheduleCallShared";
import type {ScheduleCallStrategy} from "~/domainModels/scheduleCall/scheduleCallStrategy";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import type {EventContainerWithEvents} from "~/utilities/apis/api";
import {updateInitialLead} from "~/utilities/workspaceSpecific/emotoradUpdateInitialLead";

function journeyCallEvents(journey: EventContainerWithEvents): EventContainerWithEvents["events"] {
    return Array.isArray(journey.events) ? journey.events : [];
}

export const emotoradScheduleCallStrategy: ScheduleCallStrategy = {
    callEventTemplateId: EmotoradEventTypes.call as Uuid,
    supportsInlineModal: true,
    resolveCallEventTemplate: (eventTemplates) => eventTemplates.find((template) => template.id === (EmotoradEventTypes.call as Uuid)),
    buildCallTimeSlotOptions: buildScheduleCallTimeSlotOptions,
    combineDateAndTimeSlot: combineScheduleDateAndTimeSlot,
    buildSubmitPayload: ({journey, scheduledOnIso, workspaceUserId}) => {
        const journeyValues = journey.additionalColumnValues ?? {};
        const mergedJourney: Record<Uuid, unknown> = {...journeyValues};
        const previousCallsCount = parseScheduleNumericColumn(mergedJourney[EMOTORAD__EVENT_CONTAINER__CALLS_COUNT__COLUMN_ID]);
        mergedJourney[EMOTORAD__EVENT_CONTAINER__CALLS_COUNT__COLUMN_ID] = String(previousCallsCount + 1);

        const eventColumns: Record<Uuid, unknown> = {
            [EMOTORAD__EVENT__CALLS__STATUS__COLUMN_ID]: EmotoradCallStatusEnum.SCHEDULED,
            [EMOTORAD__EVENT__CALLS__SCHEDULED_BY__COLUMN_ID]: workspaceUserId,
            [EMOTORAD__EVENT__CALLS__CURRENT_PLATFORM__COLUMN_ID]: EMOTORAD_CALL_EVENT_SUBMIT_CURRENT_PLATFORM_VALUE,
            [EMOTORAD__EVENT__CALLS__SCHEDULED_ON__COLUMN_ID]: scheduledOnIso,
            [EMOTORAD__EVENT__CALLS__LAST_CALL_DATE__COLUMN_ID]: scheduledOnIso,
        };

        copyJourneyColumnToEventWhenPresent(
            eventColumns,
            journeyValues,
            EMOTORAD__EVENT_CONTAINER__CUSTOMER_PINCODE__COLUMN_ID,
            EMOTORAD__EVENT__CALLS__CUSTOMER_PINCODE__COLUMN_ID,
        );
        copyJourneyColumnToEventWhenPresent(
            eventColumns,
            journeyValues,
            EMOTORAD__EVENT_CONTAINER__CUSTOMER_STATE__COLUMN_ID,
            EMOTORAD__EVENT__CALLS__CUSTOMER_STATE__COLUMN_ID,
        );
        copyJourneyColumnToEventWhenPresent(
            eventColumns,
            journeyValues,
            EMOTORAD__EVENT_CONTAINER__CUSTOMER_CITY__COLUMN_ID,
            EMOTORAD__EVENT__CALLS__CUSTOMER_CITY__COLUMN_ID,
        );

        const lastLoggedCallDispositionId = resolveEmotoradLastLoggedCallDispositionId(journey);
        if (lastLoggedCallDispositionId !== undefined) {
            eventColumns[EMOTORAD__EVENT__CALLS__CALL_DISPOSITION__COLUMN_ID] = lastLoggedCallDispositionId;
        }

        const latestCallEvent = journeyCallEvents(journey)
            .filter((event) => event.templateId === (EmotoradEventTypes.call as Uuid))
            .sort((a, b) => Date.parse(b.updatedAt) - Date.parse(a.updatedAt))[0];
        const remarksRaw = latestCallEvent?.additionalColumnValues[EMOTORAD__EVENT__CALLS__LOGGED_CALL_REMARKS__COLUMN_ID];
        if (typeof remarksRaw === "string" && remarksRaw.trim() !== "") {
            eventColumns[EMOTORAD__EVENT__CALLS__LOGGED_CALL_REMARKS__COLUMN_ID] = remarksRaw.trim();
        }

        return {
            journeyUpdate: {additionalColumnValues: mergedJourney},
            eventAdditionalColumnValues: eventColumns,
        };
    },
    runAfterSuccess: async ({journey, accessToken, intellsysBaseUrl, workspaceId}) => {
        const result = await updateInitialLead({
            accessToken,
            intellsysBaseUrl,
            workspaceId,
            eventContainerId: journey.id,
        });
        if (result.success !== true) {
            throw result.error;
        }
    },
};

export const emotoradListScheduleCallStrategy = emotoradScheduleCallStrategy;
